import { CSSProperties } from 'react';
import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { UserProfession } from '~/enums/UserProfession';
import { useMediaQuery } from '~/hooks';
import { ProfessionalNameRow } from '../shared/ProfessionalNameRow';
import { ActivityLevelInfo } from '../shared/ActivityLevelInfo';
import AttendanceBadgeInline from '../shared/AttendanceBadgeInline';
import { IProfessionalCardUser } from '../../interfaces';
import { BULLET } from '../shared/cardText';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/**
 * flex/minWidth/marginLeft have no DS Box prop. NOTE(future-DS): the marginLeft spaces this
 * column from the avatar in the parent <ProfessionalCard>; ideally a gap on the parent's row.
 */
const detailsColumnStyle: CSSProperties = { marginLeft: 16, flex: 1, minWidth: 0 };
/** whiteSpace has no DS Row prop; keeps each info line on one line */
const nowrapRowStyle: CSSProperties = { whiteSpace: 'nowrap' };

interface IProfessionalCardDetailsNewProps {
  user: IProfessionalCardUser;
}

export const ProfessionalCardDetailsNew = ({ user }: IProfessionalCardDetailsNewProps) => {
  const {
    id,
    fullname,
    profession,
    rating,
    isAvailable,
    yearsOfExperience,
    badges,
    activityLevel,
  } = user;
  const textColor = colors.neutral['500'];
  const hasBadges = badges && badges.length > 0;
  const [isSmallScreen] = useMediaQuery(['mlg']);

  const renderActivityLine = () => (
    <Row align="center" style={nowrapRowStyle}>
      {activityLevel && <ActivityLevelInfo activityLevel={activityLevel} isAvailable />}
      {!isAvailable && (
        <Typography color={colors.error['500']}>
          {activityLevel && <>{BULLET}</>}
          Currently Unavailable
        </Typography>
      )}
    </Row>
  );

  return (
    <Column justify="center" style={detailsColumnStyle}>
      {/* Row 1: Name + Rating + Profile */}
      <ProfessionalNameRow id={id} fullname={fullname} rating={rating} isAvailable />

      {isSmallScreen ? (
        <>
          {/* Small screen — Row 2: Profession • Experience */}
          <Row align="center" style={nowrapRowStyle}>
            <Typography color={textColor}>{UserProfession.getName(profession || '')}</Typography>
            {yearsOfExperience != null && (
              <Typography color={textColor}>
                {BULLET}
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Typography>
            )}
          </Row>

          {/* Small screen — Row 3: Attendance */}
          {hasBadges && (
            <Row align="center" gap={1} style={nowrapRowStyle}>
              <Typography color={textColor}>Attendance:</Typography>
              <AttendanceBadgeInline badges={badges} />
            </Row>
          )}

          {/* Small screen — Row 4: Activity Level */}
          {renderActivityLine()}
        </>
      ) : (
        <>
          {/* Big screen — Row 2: Profession • Experience • Attendance */}
          <Row align="center" style={nowrapRowStyle}>
            <Typography color={textColor}>{UserProfession.getName(profession || '')}</Typography>
            {yearsOfExperience != null && (
              <Typography color={textColor}>
                {BULLET}
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Typography>
            )}
            {hasBadges && (
              <Row align="center" gap={1}>
                <Typography color={textColor}>{BULLET}Attendance:</Typography>
                <AttendanceBadgeInline badges={badges} />
              </Row>
            )}
          </Row>

          {/* Big screen — Row 3: Activity Level */}
          {renderActivityLine()}
        </>
      )}
    </Column>
  );
};
